import { createContext, useContext, useState, useEffect } from 'react';
import { signupApi, signinApi, fetchMe, setToken, getToken } from '../api';

const AuthContext = createContext(null);

function normalize(user) {
  if (!user) return null;
  // Components historically read `user.uid`; keep it as an alias of the id.
  return { ...user, uid: user.id };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    async function load() {
      if (!getToken()) {
        if (active) setLoading(false);
        return;
      }
      try {
        const data = await fetchMe();
        if (active) setUser(normalize(data.user));
      } catch (_e) {
        setToken(null);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, []);

  async function signUp({ email, username, password }) {
    const data = await signupApi({ email, username, password });
    // Backend no longer issues a token on signup; the user must verify their
    // email first. Surface the pending state to the caller.
    return { verificationRequired: true, email: data?.email || email };
  }

  async function signIn({ login, password }) {
    const data = await signinApi({ login, password });
    setToken(data.token);
    const u = normalize(data.user);
    setUser(u);
    return u;
  }

  async function completeVerification({ token, user }) {
    setToken(token);
    const u = normalize(user);
    setUser(u);
    return u;
  }

  async function logOut() {
    setToken(null);
    setUser(null);
  }

  const value = { user, loading, signIn, signUp, logOut, completeVerification };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
